import { LockKeyhole } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@lightning/ui/button';
import { HowieActionButton } from '@/components/HowieActionButton';

interface InsightCaptureProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onSkip: () => void;
  onNextPractice?: () => void;
  disabled?: boolean;
  maxChars?: number;
}

export const MAX_INSIGHT_CHARS = 5000;

/** The optional close: capture a private insight or continue without one. */
export function InsightCapture({
  value,
  onChange,
  onSave,
  onSkip,
  onNextPractice,
  disabled = false,
  maxChars = MAX_INSIGHT_CHARS,
}: InsightCaptureProps) {
  const [reflecting, setReflecting] = useState(Boolean(value.trim()));
  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-how-space-deep/[0.05] bg-how-paper/55 p-6">
      <h2 className="font-body text-[24px] font-medium leading-[30px] text-how-space-deep">Practice complete.</h2>
      {!reflecting && <p className="mt-2 text-[14px] leading-[22px] text-gray-500">
        Before you move on, would you like a moment to reflect?
      </p>}
      {!reflecting ? (
        <>
        <div className="mt-6 flex flex-wrap gap-3">
          <HowieActionButton disabled={disabled} onClick={() => setReflecting(true)}>Reflect</HowieActionButton>
          {onNextPractice && <Button variant="outline" className="h-9 rounded-full border-how-space-deep/20 bg-transparent px-4 font-body text-[13px] font-normal text-how-space-deep shadow-none hover:bg-how-space-deep/5" disabled={disabled} onClick={onNextPractice}>Next practice</Button>}
        </div>
        <Button variant="link" className="mt-3 h-9 px-0 font-body text-[13px] font-normal text-how-text-secondary underline-offset-4" disabled={disabled} onClick={onSkip}>Back to Dojo</Button>
        </>
      ) : (
      <>
      <label htmlFor="practice-reflection" className="mt-6 block text-[16px] leading-6 text-how-space-deep">
        In a similar situation in your life, what is one thing you would like to try?
      </label>
      <textarea
        id="practice-reflection"
        autoFocus
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxChars}
        rows={4}
        placeholder="One action I would like to try…"
        className="mt-4 w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-[15px] leading-6 text-how-space-deep placeholder:text-gray-400 outline-none transition-colors focus:border-how-accent-warm focus:ring-2 focus:ring-how-accent-warm/20"
      />
      <p className="mt-3 flex items-center gap-1.5 text-[12px] leading-5 text-how-text-secondary">
        <LockKeyhole size={13} aria-hidden="true" />
        This will be saved privately to Your HOW.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <HowieActionButton
          type="button"
          onClick={onSave}
          disabled={!value.trim() || disabled}
        >
          {disabled ? 'Saving…' : 'Save reflection'}
        </HowieActionButton>
        <Button
          variant="ghost"
          className="h-9 rounded-full px-4 font-body text-[13px] font-normal text-how-text-secondary"
          type="button"
          onClick={onSkip}
          disabled={disabled}
        >
          Not now
        </Button>
      </div>
      </>
      )}
    </div>
  );
}

export default InsightCapture;
